// AuditFooter — quiet bottom strip on the contract details surface
// showing who created and last modified the contract. `createdBy` and
// `updatedBy` arrive as user IDs from the contract record; this
// component resolves them to display names via `useUserDisplays` (with
// a "Unknown user" fallback when a lookup misses, e.g. deleted /
// cross-tenant / unresolved IDs). When createdAt === updatedAt the
// "Last modified" segment is hidden (avoids implying an edit that did
// not happen). The "Status changed" segment and activity-log link are
// intentionally not rendered until a real history source exists.

import React, { useMemo } from "react";
import { useUserDisplays } from "../../../hooks/useUsers";
import { formatDayMonthYearLocal } from "../../../utils/formatShortDate";

interface AuditFooterProps {
  createdAt: string;
  createdBy: string;
  updatedAt: string;
  updatedBy: string;
}

// Middle-dot separator with literal whitespace either side. The literal
// spaces ensure the textContent reads "Created by X · 16 Apr 2026"
// (with spaces around the dot) so accessibility tooling and any
// snapshot/log-style readers don't see a glued "X·16 Apr 2026" string.
// The CSS padding alone is not sufficient for textContent rendering.
const Sep: React.FC = () => (
  <span data-audit-sep aria-hidden="true">
    {" · "}
  </span>
);

export function AuditFooter({
  createdAt,
  createdBy,
  updatedAt,
  updatedBy,
}: AuditFooterProps) {
  const ids = useMemo(
    () => [createdBy, updatedBy].filter((id) => id && id.length > 0),
    [createdBy, updatedBy],
  );
  const { data: userMap } = useUserDisplays(ids);

  const resolveName = (id: string): string => {
    if (!id || id.trim().length === 0) return "Unknown user";
    return userMap.get(id)?.name ?? "Unknown user";
  };

  if (!createdAt) return null;

  const showLastModified = updatedAt !== "" && updatedAt !== createdAt;

  return (
    <div
      data-testid="audit-footer"
      className="flex flex-wrap gap-x-2 gap-y-1 border-t border-slate-200 dark:border-slate-700 pt-3 px-0 text-[11px] text-slate-500 dark:text-slate-400"
    >
      <span>
        <strong className="font-semibold text-slate-600 dark:text-slate-300">
          Created
        </strong>{" "}
        by {resolveName(createdBy)}
        <Sep />
        {formatDayMonthYearLocal(createdAt)}
      </span>
      {showLastModified && (
        <>
          <Sep />
          <span>
            <strong className="font-semibold text-slate-600 dark:text-slate-300">
              Last modified
            </strong>{" "}
            by {resolveName(updatedBy)}
            <Sep />
            {formatDayMonthYearLocal(updatedAt)}
          </span>
        </>
      )}
    </div>
  );
}

export default AuditFooter;
